import { useEffect, useState, useCallback } from "react";
import { Search, SlidersHorizontal, Loader2, Compass, X } from "lucide-react";
import { Layout } from "@/components/Layout";
import { DiscoverCard } from "@/components/DiscoverCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const INTENTS = ["Find customers", "Find suppliers", "Find distributors", "Find partners", "Promote my business", "Discover businesses", "Network with business owners"];
const CONTENT = [["", "Any activity"], ["Offer", "Has Offers"], ["Requirement", "Has Requirements"], ["recent", "Recently active"]];

export default function Discover() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [f, setF] = useState({ q: "", category: "all", state: "", city: "", intent: "", content: "", gst_verified: false, sort: user ? "foryou" : "recent" });

  useEffect(() => { api.get("/onboarding/options").then(({ data }) => setCategories(data.categories)).catch((e) => console.error("Failed to load categories", e)); }, []);

  const fetchData = useCallback(async (filters, pageNum = 1) => {
    if (pageNum === 1) setLoading(true); else setLoadingMore(true);
    try {
      const params = { sort: filters.sort, limit: 12, page: pageNum };
      if (filters.q) params.q = filters.q;
      if (filters.category !== "all") params.category = filters.category;
      if (filters.state) params.state = filters.state;
      if (filters.city) params.city = filters.city;
      if (filters.intent) params.intent = filters.intent;
      if (filters.content) params.content = filters.content;
      if (filters.gst_verified) params.gst_verified = "true";
      const { data } = await api.get("/discover", { params });
      setItems((prev) => {
        if (pageNum === 1) return data.items;
        const seen = new Set(prev.map((b) => b.id));
        const fresh = data.items.filter((b) => !seen.has(b.id));
        return [...prev, ...fresh];
      });
      setTotal(data.total);
      setPage(data.page);
      setHasMore(data.has_more);
    } catch (e) { console.error("Failed to load discover results", e); if (pageNum === 1) setItems([]); } finally { setLoading(false); setLoadingMore(false); }
  }, []);

  useEffect(() => { const t = setTimeout(() => fetchData(f, 1), 300); return () => clearTimeout(t); }, [f, fetchData]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const clearAll = () => setF({ q: "", category: "all", state: "", city: "", intent: "", content: "", gst_verified: false, sort: user ? "foryou" : "recent" });
  const activeFilters = [f.category !== "all", f.state, f.city, f.intent, f.content, f.gst_verified].filter(Boolean).length;

  return (
    <Layout>
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
          <div className="flex items-center gap-3 mb-1">
            <Compass className="text-brand" size={24} />
            <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl text-slate-900">Discover Businesses</h1>
          </div>
          <p className="text-slate-500 mb-6">{user ? "Personalised for your business — " : ""}find verified partners, suppliers and customers across India.</p>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <Input data-testid="discover-search" value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search businesses, products, services, categories, cities…" className="h-14 pl-12 bg-white rounded-xl shadow-sm" />
            </div>
            <Button data-testid="discover-filters-toggle" onClick={() => setShowFilters(!showFilters)} variant="outline" className="h-14 px-5 gap-2 bg-white border-slate-200 font-semibold">
              <SlidersHorizontal size={18} /> Filters{activeFilters > 0 && <span className="rounded-full bg-brand text-white text-xs px-2 py-0.5">{activeFilters}</span>}
            </Button>
          </div>

          {showFilters && (
            <div data-testid="discover-filter-panel" className="mt-4 bg-white border border-slate-200 rounded-xl p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div><label className="text-xs font-bold text-slate-500 uppercase">Category</label>
                <Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="filter-category" className="mt-1 bg-slate-50 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="text-xs font-bold text-slate-500 uppercase">State</label>
                <Input data-testid="filter-state" value={f.state} onChange={(e) => set("state", e.target.value)} placeholder="e.g. Maharashtra" className="mt-1 bg-slate-50 h-10" /></div>
              <div><label className="text-xs font-bold text-slate-500 uppercase">City</label>
                <Input data-testid="filter-city" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Mumbai" className="mt-1 bg-slate-50 h-10" /></div>
              <div><label className="text-xs font-bold text-slate-500 uppercase">Business Intent</label>
                <Select value={f.intent || "any"} onValueChange={(v) => set("intent", v === "any" ? "" : v)}><SelectTrigger data-testid="filter-intent" className="mt-1 bg-slate-50 h-10"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent><SelectItem value="any">Any intent</SelectItem>{INTENTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="text-xs font-bold text-slate-500 uppercase">Activity</label>
                <Select value={f.content || "any"} onValueChange={(v) => set("content", v === "any" ? "" : v)}><SelectTrigger data-testid="filter-content" className="mt-1 bg-slate-50 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTENT.map(([v, l]) => <SelectItem key={v || "any"} value={v || "any"}>{l}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="text-xs font-bold text-slate-500 uppercase">Sort</label>
                <Select value={f.sort} onValueChange={(v) => set("sort", v)}><SelectTrigger data-testid="filter-sort" className="mt-1 bg-slate-50 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{user && <SelectItem value="foryou">For you</SelectItem>}<SelectItem value="recent">Recently active</SelectItem><SelectItem value="newest">Newest</SelectItem><SelectItem value="followers">Most followed</SelectItem></SelectContent></Select></div>
              <label className="flex items-center gap-2 mt-6 cursor-pointer" data-testid="filter-verified">
                <input type="checkbox" checked={f.gst_verified} onChange={(e) => set("gst_verified", e.target.checked)} className="h-4 w-4 accent-[#0F3D8C]" />
                <span className="text-sm font-semibold text-slate-700">GST Verified only</span></label>
              <div className="flex items-end">
                <Button data-testid="clear-filters" onClick={clearAll} variant="ghost" className="gap-1.5 text-slate-500"><X size={15} /> Clear all</Button></div>
            </div>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-8">
        <p className="text-sm text-slate-400 mb-4">{total} businesses found</p>
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={30} /></div>
        ) : items.length === 0 ? (
          <div data-testid="discover-empty" className="py-16 text-center text-slate-500">No businesses match your search. Try clearing filters.</div>
        ) : (
          <>
            <div data-testid="discover-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.map((b) => <DiscoverCard key={b.id} business={b} onChange={() => fetchData(f, 1)} />)}
            </div>
            {hasMore && (
              <div className="mt-10 flex justify-center">
                <Button data-testid="discover-load-more" onClick={() => fetchData(f, page + 1)} disabled={loadingMore} variant="outline" className="h-12 px-8 gap-2 border-slate-300 font-semibold">
                  {loadingMore ? <><Loader2 className="animate-spin" size={18} /> Loading…</> : `Load More (${total - items.length} more)`}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
